/**
 * Sign → PUT → capture, as one hook (block 06).
 *
 * The three-step dance is here so that no client re-implements it: the webapp
 * drop-zone, block 12's extension screenshots and block 25's WhatsApp media are
 * all this hook with a different `capture`. That is why the payload is an
 * arbitrary `Capture` minus `storagePath` rather than a caption — block 12 adds
 * `url` and needs nothing else from here.
 *
 * The capture step delegates to `useCreateNook`, so an image nook gets the same
 * optimistic row, the same per-attempt `Idempotency-Key` and the same list
 * invalidation as every other capture. A second, parallel capture path is the
 * thing this composition exists to avoid.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getNookClient } from "../client.js";
import { putSignedUpload } from "../endpoints/uploads.js";
import { queryKeys } from "../query-keys.js";
import { useCreateNook } from "./use-create-nook.js";
export function useUploadImage() {
    const queryClient = useQueryClient();
    const create = useCreateNook();
    const [progress, setProgress] = useState(null);
    const mutation = useMutation({
        mutationFn: async ({ file, capture }) => {
            setProgress(0);
            const signed = await getNookClient().signUpload({
                // A pasted or generated blob has no name; the backend sanitises
                // whatever it is given, but it does require something.
                filename: file.name || "image",
                contentType: file.type,
                sizeBytes: file.size,
            });
            await putSignedUpload(signed.uploadUrl, file, setProgress);
            return create.mutateAsync({ ...capture, storagePath: signed.storagePath });
        },
        // No retry. A failed 20MB PUT is not something to silently do again, and
        // the capture step retries on its own with a stable key.
        retry: 0,
        onSettled() {
            setProgress(null);
            void queryClient.invalidateQueries({ queryKey: queryKeys.storage.usage() });
        },
    });
    /** `null` when idle, 0-100 while the bytes are moving. */
    return { ...mutation, progress };
}
//# sourceMappingURL=use-upload-image.js.map